// CalificarEquipo — quick post-closure peer rating. Each teammate the caller
// worked with gets one overall star (1-5), skippable. Internal/ops-only in
// Phase 1; surfaced on profiles weekly in Phase 2. Writes the Calificaciones
// ledger via /events/:id/peer-ratings.
//
// Shown as a gate before ResultadosEvento: the captain lands here right after
// finalizing, and any staffer reaching a completed event's results passes
// through it. Submit-once: a rating can't be changed after it's sent, and the
// screen never reopens once the caller went through it (alreadyReviewed) — if
// there is nothing left to rate it advances to results immediately.

import { useEffect, useState } from 'preact/hooks';
import { getRateableTeammates, submitPeerRatings, type RateableTeammate } from '../lib/api';
import { haptic } from '../lib/telegram';

interface CalificarEquipoProps {
  eventId: string;
  eventTitle?: string;
  /** Called when the step is finished (submitted, skipped, or nothing to rate). */
  onDone: () => void;
}

type Stage = 'loading' | 'ready' | 'submitting' | 'error';

export function CalificarEquipo({ eventId, eventTitle, onDone }: CalificarEquipoProps) {
  const [stage, setStage] = useState<Stage>('loading');
  const [teammates, setTeammates] = useState<RateableTeammate[]>([]);
  const [stars, setStars] = useState<Record<string, number>>({});
  const [skipped, setSkipped] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await getRateableTeammates(eventId);
        // Submit-once: only teammates not yet rated are shown, and the screen
        // never reopens after the caller went through it. Nothing left to rate
        // → advance straight to results, no empty screen.
        const unrated = r.ready && !r.alreadyReviewed ? r.teammates.filter((t) => !t.alreadyRated) : [];
        if (unrated.length === 0) { onDone(); return; }
        setTeammates(unrated);
        setStage('ready');
      } catch (e) {
        setError((e as Error).message);
        setStage('error');
      }
    })();
  }, [eventId]);

  function setStar(staffId: string, n: number) {
    haptic('light');
    setStars((prev) => ({ ...prev, [staffId]: n }));
    setSkipped((prev) => (prev[staffId] ? { ...prev, [staffId]: false } : prev)); // rating un-skips
  }

  function toggleSkip(staffId: string) {
    haptic('light');
    setSkipped((prev) => ({ ...prev, [staffId]: !prev[staffId] }));
    setStars((prev) => {
      if (!prev[staffId]) return prev;
      const c = { ...prev }; delete c[staffId]; return c; // skipping clears any star
    });
  }

  // "Listo" — submit the set stars and mark the event reviewed (closes the
  // request for good). An empty payload still calls the endpoint so the event
  // clears from the pending list (= "I went through it and skipped everyone").
  async function submit() {
    const payload = teammates
      .filter((t) => stars[t.staffId])
      .map((t) => ({ targetStaffId: t.staffId, estrellas: stars[t.staffId] }));
    setStage('submitting');
    haptic('light');
    try {
      const r = await submitPeerRatings(eventId, payload);
      // A failed write keeps the request open server-side (no reviewed marker)
      // — surface it so the rater knows to come back instead of assuming done.
      if (!r.ok) {
        setError('No se pudo guardar una o más calificaciones. Vuelve a intentarlo en un momento — lo que sí se guardó no se pierde.');
        setStage('error');
        haptic('error');
        return;
      }
      haptic('success');
      onDone();
    } catch (e) {
      setError((e as Error).message);
      setStage('error');
      haptic('error');
    }
  }

  if (stage === 'loading') {
    return (
      <div class="event-chat-screen">
        <Header eventTitle={eventTitle} eventId={eventId} onSkip={onDone} />
        <div class="mireporte-body"><div class="loading"><span class="spinner" /></div></div>
      </div>
    );
  }

  if (stage === 'error') {
    return (
      <div class="event-chat-screen">
        <Header eventTitle={eventTitle} eventId={eventId} onSkip={onDone} />
        <div class="mireporte-body">
          <div class="mireporte-error">⚠️ {error || 'Algo no funcionó.'}</div>
          <button class="btn primary" onClick={onDone}>Continuar</button>
        </div>
      </div>
    );
  }

  return (
    <div class="event-chat-screen">
      <Header eventTitle={eventTitle} eventId={eventId} onSkip={onDone} />
      <div class="mireporte-body">
        <h3>Califica a tu equipo</h3>
        <p class="mireporte-meta">
          Califica a los compañeros con quienes trabajaste de cerca en este evento.
          Toca <strong>Saltar</strong> en los que casi no interactuaste. Es anónima
          y <strong>se envía una sola vez</strong> — no se puede cambiar después.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
          {teammates.map((t) => {
            const isSkipped = !!skipped[t.staffId];
            return (
              <div key={t.staffId} class="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)', opacity: isSkipped ? 0.5 : 1 }}>
                <div style={{ minWidth: 0 }}>
                  <div class="lt-title" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.nombre || t.staffId}</div>
                  {t.rol && <div class="lt-sub">{t.rol}</div>}
                </div>
                {isSkipped ? (
                  <button
                    onClick={() => toggleSkip(t.staffId)}
                    style={{ background: 'none', border: 'none', color: 'var(--color-hint, #8e8e93)', cursor: 'pointer', fontSize: 'var(--font-size-sm)', flexShrink: 0, whiteSpace: 'nowrap' }}
                  >
                    Saltado · deshacer
                  </button>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexShrink: 0 }}>
                    <StarRow value={stars[t.staffId] || 0} onPick={(n) => setStar(t.staffId, n)} />
                    <button
                      aria-label="Saltar este compañero"
                      onClick={() => toggleSkip(t.staffId)}
                      style={{ background: 'none', border: 'none', color: 'var(--color-hint, #8e8e93)', cursor: 'pointer', fontSize: 'var(--font-size-xs)' }}
                    >
                      Saltar
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div class="mireporte-actions" style={{ marginTop: 'var(--space-4)' }}>
          <button class="btn primary" disabled={stage === 'submitting'} onClick={submit}>
            {stage === 'submitting' ? 'Enviando...' : 'Listo'}
          </button>
          <button class="btn ghost" disabled={stage === 'submitting'} onClick={onDone}>Recordarme luego</button>
        </div>
      </div>
    </div>
  );
}

function StarRow({ value, onPick }: { value: number; onPick: (n: number) => void }) {
  return (
    <div style={{ display: 'flex', gap: '2px', flexShrink: 0 }} role="radiogroup" aria-label="Calificación">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          aria-label={`${n} estrella${n === 1 ? '' : 's'}`}
          aria-checked={value === n}
          onClick={() => onPick(n)}
          style={{
            background: 'none',
            border: 'none',
            padding: '2px',
            fontSize: 'var(--font-size-xl)',
            lineHeight: 1,
            cursor: 'pointer',
            color: n <= value ? 'var(--brand-gold)' : 'var(--color-hint, #c7c7cc)',
          }}
        >
          {n <= value ? '★' : '☆'}
        </button>
      ))}
    </div>
  );
}

function Header({ eventId, eventTitle, onSkip }: { eventId: string; eventTitle?: string; onSkip: () => void }) {
  return (
    <header class="event-chat-header">
      <button class="event-chat-back" onClick={onSkip} aria-label="Saltar">←</button>
      <div class="event-chat-titles">
        <div class="event-chat-title">Califica a tu equipo · {eventTitle || eventId}</div>
        <div class="event-chat-sub">Una estrella por compañero · anónimo</div>
      </div>
    </header>
  );
}
